/**
 * CPU mesh building. Vertex layout is pos(3) | normal(3) | uv(2) = 32 bytes,
 * shared by every mesh pipeline so geometry can live in one atlas-style buffer.
 */

import { compose, type Mat4 } from '../core/math';

/** pos(3) | normal(3) | uv(2) | material(1) */
export const VERT_FLOATS = 9;
export const VERT_BYTES = VERT_FLOATS * 4;

/**
 * Material slots read by the mesh shader.
 *
 * `drive` sits above `glow` rather than between two existing slots: the shader
 * picks its weights with smoothstep ramps between neighbours, so a value tucked
 * into a gap would half-belong to both sides. Anything past 0.96 is already
 * fully glowing, which leaves the range above it free to mean something more.
 */
export const MAT = {
  paint: 0.0,
  metal: 0.35,
  rubber: 0.55,
  organic: 0.75,
  /**
   * Organic AND THIN. Shading-identical to `organic` — it sits inside the same
   * weight window and picks the same detail layer — but a blade of weed and a
   * boulder are both "organic" and only one of them lets light through it.
   * Without a second tag the translucency term had no way to tell them apart
   * and lit the undersides of the rocks instead of the plants.
   */
  leaf: 0.82,
  glow: 1.0,
  /**
   * AIR, INSIDE A HULL. The decks, ceilings, bulkheads and liners of a habitat
   * cabin, seen through its glass.
   *
   * It needs its own tag because `glow` takes the FULL blue ambient dome on top
   * of whatever it emits — which is right for a strip light hanging in the sea
   * and wrong for a room with air in it. Lit blue and fogged like water, a
   * cabin reads as a flooded capsule however warm its emissive is.
   *
   * 1.08 sits in the one free band on the scale: above `wGlow`'s 0.96
   * saturation so it still takes the glass roughness and F0, and below
   * `wDrive`'s `step(1.15)` so it cannot be mistaken for the reactor.
   */
  cabin: 1.08,
  /** Glowing, and warm: the drive is the only hot thing on the boat. */
  drive: 1.4,
  /**
   * FOAM. A thin white scattering sheet, drawn through the translucent pass.
   *
   * Deliberately above everything else on this scale — `fsJelly` branches on
   * the material tag to tell a bell from a tentacle from an oral arm, and its
   * bands run to 0.90. Sitting at 1.9 means the foam branch can be tested
   * first and cannot be reached by any value the jellyfish use, so adding it
   * could not move a single pixel of one.
   */
  foam: 1.9,
  /**
   * NATIVE METAL ORE. Iron, copper, titanium — opaque, no interior.
   *
   * A slot of its own rather than a `metallic` value, because `metallic` is a
   * shading number and this is a CLASSIFICATION. Deriving one from the other
   * gave iron 35% of the gem treatment: an interior, dispersion and
   * translucency, on a lump of metal. Both ore slots sit above 1.6 so both
   * refuse the reference-art detail layer and both escape `wDrive`; the gem
   * terms are then gated at 1.73, which only `crystal` clears.
   */
  oreMetal: 1.65,
  /**
   * ORE. Faceted crystal and native metal, on its own slot rather than folded
   * into `glow`.
   *
   * It sits above `drive` because the crystal branch has to be able to opt OUT
   * of things every other mesh takes unconditionally — the reference-art detail
   * layer most of all, which is a photograph of coral polyps and reads as
   * lichen when it is tiled across a quartz facet. A slot inside the existing
   * ramps would half-belong to its neighbours and could not refuse anything.
   *
   * Below `foam` at 1.9, which is a translucent-pass tag and can never be seen
   * by `fsMesh`, so the two cannot collide even by accident. Above
   * `oreMetal`, which is the line the gem terms are gated on.
   */
  crystal: 1.8,
} as const;

/**
 * Half-extents of a built mesh, from its own vertices.
 *
 * So a thing that has to know how big a mesh is can ask it, rather than
 * carrying a copy of the answer that was right when it was written.
 */
export function meshHalfExtents(m: MeshData): [number, number, number] {
  let hx = 0, hy = 0, hz = 0;
  for (let i = 0; i < m.vertices.length; i += VERT_FLOATS) {
    hx = Math.max(hx, Math.abs(m.vertices[i]!));
    hy = Math.max(hy, Math.abs(m.vertices[i + 1]!));
    hz = Math.max(hz, Math.abs(m.vertices[i + 2]!));
  }
  return [hx, hy, hz];
}

export interface MeshData {
  vertices: Float32Array<ArrayBuffer>;
  indices: Uint32Array<ArrayBuffer>;
  /**
   * Optional vertical index of the index buffer, for meshes whose triangles are
   * emitted sorted in y — terrain strips. `sliceY[k]` is the bottom of slice k,
   * `sliceStart[k]` its first index, and both run one entry past the last slice
   * so `sliceY[n]` is the top of the mesh and `sliceStart[n]` its index count.
   * Lets the renderer draw only the y band the camera can actually see.
   */
  sliceY?: Float32Array<ArrayBuffer>;
  sliceStart?: Uint32Array<ArrayBuffer>;
}

export interface GpuMesh {
  vbo: GPUBuffer;
  ibo: GPUBuffer;
  indexCount: number;
  sliceY: Float32Array<ArrayBuffer> | null;
  sliceStart: Uint32Array<ArrayBuffer> | null;
}

export class MeshBuilder {
  verts: number[] = [];
  idx: number[] = [];
  private xf: Mat4 | null = null;
  private mat = 0;

  get vertexCount() {
    return this.verts.length / VERT_FLOATS;
  }

  /** Material slot applied to every vertex pushed from now on. */
  material(m: number) {
    this.mat = m;
    return this;
  }

  /** Apply this transform to everything pushed until `popTransform()`. */
  transform(
    px: number, py: number, pz: number,
    rx = 0, ry = 0, rz = 0,
    sx = 1, sy = sx, sz = sx,
  ) {
    this.xf = compose(new Float32Array(16), px, py, pz, rx, ry, rz, sx, sy, sz);
    return this;
  }

  popTransform() {
    this.xf = null;
    return this;
  }

  vertex(x: number, y: number, z: number, nx: number, ny: number, nz: number, u: number, v: number) {
    const m = this.xf;
    if (m) {
      const tx = m[0] * x + m[4] * y + m[8] * z + m[12];
      const ty = m[1] * x + m[5] * y + m[9] * z + m[13];
      const tz = m[2] * x + m[6] * y + m[10] * z + m[14];
      // Rotation-only normal transform (our transforms use uniform-ish scale).
      const rnx = m[0] * nx + m[4] * ny + m[8] * nz;
      const rny = m[1] * nx + m[5] * ny + m[9] * nz;
      const rnz = m[2] * nx + m[6] * ny + m[10] * nz;
      const l = Math.hypot(rnx, rny, rnz) || 1;
      this.verts.push(tx, ty, tz, rnx / l, rny / l, rnz / l, u, v, this.mat);
    } else {
      this.verts.push(x, y, z, nx, ny, nz, u, v, this.mat);
    }
    return this.vertexCount - 1;
  }

  tri(a: number, b: number, c: number) {
    this.idx.push(a, b, c);
  }

  quad(a: number, b: number, c: number, d: number) {
    this.idx.push(a, b, c, a, c, d);
  }

  build(): MeshData {
    return { vertices: new Float32Array(this.verts), indices: new Uint32Array(this.idx) };
  }
}

/**
 * Surface of revolution about the X axis. `profile(t)` returns [x, radius] for
 * t in [0, 1]; normals come from the profile's slope. This builds hulls, noses,
 * nacelles and tentacle segments from one primitive.
 */
/**
 * `squash` flattens the revolve in Z, and `power` makes its cross-section a
 * superellipse rather than a circle.
 *
 * The reference hull is a rounded SQUARE in section, not a tube — measured at
 * r_diagonal / r_axial ≈ 1.07, which is an exponent near 2.5. A circle reads as
 * a pipe; the rounded square is most of why the reference looks like a built
 * object with plating on it rather than an extrusion.
 */
export function revolveX(
  mb: MeshBuilder,
  profile: (t: number) => [number, number],
  steps = 24,
  seg = 20,
  power = 2,
  squash = 1,
) {
  const base = mb.vertexCount;
  const stride = seg + 1;
  for (let r = 0; r <= steps; r++) {
    const t = r / steps;
    const [x, rad] = profile(t);
    const e = 1 / (steps * 4);
    const [xa, ra] = profile(Math.max(0, t - e));
    const [xb, rb] = profile(Math.min(1, t + e));
    const dx = xb - xa, dr = rb - ra;
    const nl = Math.hypot(dx, dr) || 1;
    // Outward normal of the profile curve, rotated around X.
    const nAxial = -dr / nl;
    const nRadial = dx / nl;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      let ca = Math.cos(a), sa = Math.sin(a);
      if (power !== 2) {
        // Superellipse: |cos|^(2/n) with the sign kept. n = 2 is the circle.
        const k = 2 / power;
        ca = Math.sign(ca) * Math.pow(Math.abs(ca), k);
        sa = Math.sign(sa) * Math.pow(Math.abs(sa), k);
      }
      mb.vertex(
        x, ca * rad, sa * rad * squash,
        nAxial, ca * nRadial, sa * nRadial * (squash ? 1 / squash : 1),
        i / seg, t,
      );
    }
  }
  for (let r = 0; r < steps; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }
}

/** Capsule along the X axis. */
export function capsuleX(mb: MeshBuilder, halfLen: number, radius: number, seg = 20, steps = 20) {
  revolveX(
    mb,
    (t) => {
      const total = halfLen * 2 + radius * 2;
      const s = t * total - radius - halfLen;
      if (s < -halfLen) {
        const k = (s + halfLen) / radius; // -1..0
        return [s, radius * Math.sqrt(Math.max(0, 1 - k * k))];
      }
      if (s > halfLen) {
        const k = (s - halfLen) / radius; // 0..1
        return [s, radius * Math.sqrt(Math.max(0, 1 - k * k))];
      }
      return [s, radius];
    },
    steps,
    seg,
  );
}

/** Rounded box (bevelled corners via a simple inset shell). */
export function box(mb: MeshBuilder, hx: number, hy: number, hz: number) {
  const faces: [number[], number[], number[]][] = [
    [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
    [[-1, 0, 0], [0, 1, 0], [0, 0, -1]],
    [[0, 1, 0], [0, 0, 1], [1, 0, 0]],
    [[0, -1, 0], [0, 0, -1], [1, 0, 0]],
    [[0, 0, 1], [0, 1, 0], [-1, 0, 0]],
    [[0, 0, -1], [0, 1, 0], [1, 0, 0]],
  ];
  for (const [n, u, v] of faces) {
    const b = mb.vertexCount;
    for (let j = 0; j < 4; j++) {
      const su = j === 0 || j === 3 ? -1 : 1;
      const sv = j < 2 ? -1 : 1;
      mb.vertex(
        (n[0] + u[0] * su + v[0] * sv) * hx,
        (n[1] + u[1] * su + v[1] * sv) * hy,
        (n[2] + u[2] * su + v[2] * sv) * hz,
        n[0], n[1], n[2],
        su * 0.5 + 0.5, sv * 0.5 + 0.5,
      );
    }
    mb.quad(b, b + 1, b + 2, b + 3);
  }
}

/** Cylinder along Y, optionally tapered. */
export function cylinderY(mb: MeshBuilder, r0: number, r1: number, h: number, seg = 16, caps = true) {
  const base = mb.vertexCount;
  for (let ring = 0; ring < 2; ring++) {
    const rr = ring ? r1 : r0;
    const y = ring ? h * 0.5 : -h * 0.5;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      mb.vertex(Math.cos(a) * rr, y, Math.sin(a) * rr, Math.cos(a), (r0 - r1) / h, Math.sin(a), i / seg, ring);
    }
  }
  for (let i = 0; i < seg; i++) {
    const a = base + i;
    mb.quad(a, a + seg + 1, a + seg + 2, a + 1);
  }
  if (caps) {
    for (let ring = 0; ring < 2; ring++) {
      const rr = ring ? r1 : r0;
      const y = ring ? h * 0.5 : -h * 0.5;
      const ny = ring ? 1 : -1;
      const c = mb.vertex(0, y, 0, 0, ny, 0, 0.5, 0.5);
      const start = mb.vertexCount;
      for (let i = 0; i <= seg; i++) {
        const a = (i / seg) * Math.PI * 2;
        mb.vertex(Math.cos(a) * rr, y, Math.sin(a) * rr, 0, ny, 0, 0.5, 0.5);
      }
      for (let i = 0; i < seg; i++) {
        if (ring) mb.tri(c, start + i, start + i + 1);
        else mb.tri(c, start + i + 1, start + i);
      }
    }
  }
}

export function sphere(mb: MeshBuilder, radius: number, seg = 18, rings = 12) {
  const base = mb.vertexCount;
  for (let r = 0; r <= rings; r++) {
    const phi = (r / rings) * Math.PI;
    const y = Math.cos(phi), rr = Math.sin(phi);
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const nx = Math.cos(a) * rr, nz = Math.sin(a) * rr;
      mb.vertex(nx * radius, y * radius, nz * radius, nx, y, nz, i / seg, r / rings);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }
}

/** Flat quad in the XY plane facing +Z — used for fins, blades and panels. */
export function plate(mb: MeshBuilder, hx: number, hy: number) {
  const b = mb.vertexCount;
  mb.vertex(-hx, -hy, 0, 0, 0, 1, 0, 0);
  mb.vertex(hx, -hy, 0, 0, 0, 1, 1, 0);
  mb.vertex(hx, hy, 0, 0, 0, 1, 1, 1);
  mb.vertex(-hx, hy, 0, 0, 0, 1, 0, 1);
  mb.quad(b, b + 1, b + 2, b + 3);
  const c = mb.vertexCount;
  mb.vertex(-hx, -hy, 0, 0, 0, -1, 0, 0);
  mb.vertex(-hx, hy, 0, 0, 0, -1, 0, 1);
  mb.vertex(hx, hy, 0, 0, 0, -1, 1, 1);
  mb.vertex(hx, -hy, 0, 0, 0, -1, 1, 0);
  mb.quad(c, c + 1, c + 2, c + 3);
}

export function uploadMesh(device: GPUDevice, data: MeshData, label?: string): GpuMesh {
  const vbo = device.createBuffer({
    size: data.vertices.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    label: label && `${label}:vbo`,
  });
  device.queue.writeBuffer(vbo, 0, data.vertices);
  const ibo = device.createBuffer({
    size: Math.ceil(data.indices.byteLength / 4) * 4,
    usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    label: label && `${label}:ibo`,
  });
  device.queue.writeBuffer(ibo, 0, data.indices);
  return {
    vbo, ibo,
    indexCount: data.indices.length,
    sliceY: data.sliceY ?? null,
    sliceStart: data.sliceStart ?? null,
  };
}

export const TERRAIN_VERTEX_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: VERT_BYTES,
  attributes: [
    { shaderLocation: 0, offset: 0, format: 'float32x3' },
    { shaderLocation: 1, offset: 12, format: 'float32x3' },
    { shaderLocation: 2, offset: 24, format: 'float32x2' },
    // FREE. The stride has always been nine floats and this one has always been
    // written — as zero, by every vertex, read by nothing. Declaring it costs no
    // memory and no bandwidth; the bytes were already being fetched. See the
    // mesher's `up`: how much a surface faces the sky.
    { shaderLocation: 3, offset: 32, format: 'float32' },
  ],
};

export const MESH_VERTEX_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: VERT_BYTES,
  attributes: [
    { shaderLocation: 0, offset: 0, format: 'float32x3' },
    { shaderLocation: 1, offset: 12, format: 'float32x3' },
    { shaderLocation: 2, offset: 24, format: 'float32x2' },
    { shaderLocation: 3, offset: 32, format: 'float32' },
  ],
};
